import React, {useMemo, useState} from 'react';
import {useTranslate} from "../../../system/useTranslate";
import Icon from "../../../system/components/icon";
import {Accordion, AccordionTab} from "primereact/accordion";
import {DataView} from "primereact/dataview";
import QuickCreateInputText from "./quick-create-inputtext";
import Avatar from "../../../system/components/avatar";
import {isPast, timeSince} from "../../../system/utils";
import TaskFocusControls from "../../../system/focus/task-focus-controls";

function AccordionDataView({
                               tabs,
                               activeIndex,
                               onRowClick,
                               columns,              // ✅ جدید: تعریف ستون‌ها
                               rowClassName,         // (اختیاری) برای اضافه کردن کلاس به ردیف
                               headerContainerClass, // (اختیاری) کلاس کانتینر هدر
                               rowContainerClass,    // (اختیاری) کلاس کانتینر ردیف
                           }) {
    const [expandedTabs, setExpandedTabs] = useState(activeIndex);
    const translate = useTranslate();

    // ✅ ستون‌های پیش‌فرض = دقیقاً همون چیزی که قبل داشتی
    const defaultColumns = useMemo(() => ([
        {
            key: "spacer",
            header: "",
            headerClassName: "w-4rem",
            cellClassName: "w-4rem",
            render: () => null,
        },
        {
            key: "title",
            header: translate("task-title"),
            headerClassName: "flex-1",
            cellClassName: "flex-1",
            render: (row) => <div className="flex align-items-center gap-2"><span className="inline-flex align-items-center gap-2" dir="ltr"><span dir="rtl">{row.title}</span><TaskFocusControls task={row} compact/></span></div>,
        },
        {
            key: "assignee",
            header: translate("assignee"),
            headerClassName: "w-7rem",
            cellClassName: "w-7rem",
            render: (row) => <Avatar memberData={row.assignee?.member} />,
        },
        {
            key: "dueDate",
            header: translate("dueDate"),
            headerClassName: "w-7rem",
            cellClassName: "w-7rem text-xs",
            cellStyle: (row) => ({
                // استایل رو مثل قبل نگه داشتیم (کلاسش هم پایین اعمال میشه)
            }),
            cellClassNameDynamic: (row) =>
                `w-7rem text-xs ${isPast(new Date(row.dueDate).getTime() / 1000, 1, "days") ? "text-red-500" : ""}`,
            render: (row) => (row.dueDate ? timeSince(new Date(row.dueDate).getTime() / 1000) : null),
        },
        {
            key: "projects",
            header: translate("projects"),
            headerClassName: "w-7rem",
            cellClassName: "w-7rem",
            render: (row) => (
                <div className="flex flex-wrap gap-1">
                    {row.projects?.map((p) => (
                        <span key={p.id ?? p.title} className="border-round px-1 border-1 border-200 surface-100 text-2xs">
              {p.title}
            </span>
                    ))}
                </div>
            ),
        },
        {
            key: "team",
            header: translate("team"),
            headerClassName: "w-7rem",
            cellClassName: "w-7rem",
            render: (row) => row.taskStatus,
        },
        {
            key: "access",
            header: translate("access"),
            headerClassName: "w-7rem",
            cellClassName: "w-7rem",
            render: (row) => row.taskStatus,
        },
    ]), [translate]);

    const cols = columns?.length ? columns : defaultColumns;

    const ItemTemplate = (row) => {
        const baseRowClass =
            `w-full flex align-items-center border-bottom-1 border-200 ${onRowClick&&"cursor-pointer"} text-sm p-2 ` +
            `${row.taskStatus === "done" ? "line-through surface-100 opacity-60 text-300" : ""} ` +
            `${rowClassName ? (typeof rowClassName === "function" ? rowClassName(row) : rowClassName) : ""}`;

        return (
            <div className={`${baseRowClass} ${rowContainerClass ?? ""}`} onClick={() => {onRowClick?.(row);}}>
                {cols.map((col) => {
                    const headerCls = col.cellClassName ?? "";
                    const dynCls = col.cellClassNameDynamic ? col.cellClassNameDynamic(row) : "";
                    const cls = `${headerCls} ${dynCls}`.trim();

                    const content = typeof col.render === "function" ? col.render(row) : col.valueKey ? row?.[col.valueKey] : null;

                    return (<span key={col.key} className={`${col.onCellClick&&"cursor-pointer"} ${cls}`} style={typeof col.cellStyle === "function" ? col.cellStyle(row) : col.cellStyle} onClick={()=>{col.onCellClick?.(row)}}>{content}</span>);
                })}
            </div>
        );
    };

    const headerTemplate = (options) => {
        return (
            <div className={`text-sm flex align-items-center ${options.headerClassName}`}>
                <Icon
                    icon={`${options.pt.expanded ? "mi mi:keyboard_arrow_down" : "mi mi:keyboard_arrow_left"}`}
                    className="text-xl p-3 pl-1"
                />
                <span>{options.header}</span>
            </div>
        );
    };

    return (
        <div className="overflow-auto h-full">
            {/* Header Row */}
            <div className={`flex surface-200 border-bottom-1 border-300 p-3 ${headerContainerClass ?? ""}`}>
                {cols.map((col) => (
                    <span key={col.key} className={col.headerClassName ?? col.cellClassName ?? ""}>{col.header}</span>
                ))}
            </div>

            {tabs?.length === 1 ? (
                <div className="flex flex-column">
                    {tabs.map((tab, index) => (
                        <React.Fragment key={tab.id ?? index}>
                            <DataView emptyMessage={<></>} value={tab.value} layout="list" itemTemplate={ItemTemplate} />
                            {tab.quickCreateCallback && (
                                <QuickCreateInputText
                                    onKeyDownEnter={(e) => {
                                        tab.quickCreateCallback(e);
                                    }}
                                    icon={"mi mi:add"}
                                    placeholder={tab.placeholder}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            ) : (
                <Accordion
                    onTabChange={(e) => {
                        setExpandedTabs(e.index);
                    }}
                    // pt={{header:()=>({style: {padding:"unset", borderRadius:"unset"}})}}
                    collapseIcon={<></>}
                    expandIcon={<></>}
                    multiple
                    activeIndex={expandedTabs}>
                    {tabs.map((tab, index) => (
                        <AccordionTab
                            key={tab.id ?? index}
                            headerClassName={`p-0 ${tab.className}`}
                            headerTemplate={headerTemplate}
                            pt={{content:()=>({style:{padding:"unset", borderBottomRightRadius:"unset", borderBottomLeftRadius:"unset"}}), root:()=>({style: {padding:"unset", marginBottom:"unset"}}),headerAction:()=>({style: {padding:"unset", borderRadius:"unset"}}) ,expanded: expandedTabs.findIndex((i) => i === index) >= 0 }}
                            header={`${tab.title} ${tab?.value.length>0?`(${tab?.value.length})`:""}`}
                            contentClassName="border-noround p-0"
                            className="mb-0">
                            <DataView emptyMessage={<></>} value={tab.value} layout="list" itemTemplate={ItemTemplate} />
                            {tab.quickCreateCallback && (
                                <QuickCreateInputText
                                    onKeyDownEnter={(e) => {
                                        tab.quickCreateCallback(e);
                                    }}
                                    icon={"mi mi:add"}
                                    placeholder={tab.placeholder}
                                />
                            )}
                        </AccordionTab>
                    ))}
                </Accordion>
            )}
        </div>
    );
}



export default AccordionDataView;
